import AudioService from '@services/AudioService';
import { loadJwPlayer } from '@services/JwPlayerService';
import { IS_SIMULATION } from '@utilities/browser';
import { Trace } from '@utilities/logging';

export type AudioPlayerPluginOptions = {
  interval?: number;
  skin?: string;
  activecolor?: string;
  cookieName?: string;
  playerId?: string;
  title?: string;
  streamUrl?: string;
  testStreamUrl?: string;
};

const defaults: AudioPlayerPluginOptions = {
  interval: 30000,
  skin: 'seven',
  activecolor: '#ff0046',
  cookieName: 'audio-active',
  playerId: 'mc-listen-live-player',
};

export default class AudioPlayerPlugin {
  options: AudioPlayerPluginOptions;
  dom: HTMLElement;
  interval: NodeJS.Timeout;
  player: jwplayer.JWPlayer;
  streamUrl: string;

  constructor(dom: HTMLElement, options: AudioPlayerPluginOptions) {
    this.options = {
      ...defaults,
      ...options,
    };

    this.dom = dom;

    this.interval = setInterval(() => {
      AudioService.open = true;
    }, 1000);

    window.addEventListener('beforeunload', this._exitFunction, { capture: true });
  }

  init() {
    if (this.options.streamUrl) {
      this._initPlayer(this.options.streamUrl);
      return;
    }

    AudioService.subscribe(this.options.interval, this.onPoll, this);
  }

  onPoll = ({ active, streamUrl }: { active: boolean; streamUrl: string }) => {
    if (IS_SIMULATION) {
      streamUrl = this.options.testStreamUrl ?? streamUrl;
    }

    active ? this._initPlayer(streamUrl) : this._disablePlayer();
  };

  async _initPlayer(streamUrl: string) {
    // if already inited and the stream url is the same exit
    if (this.player && this.streamUrl === streamUrl) return;

    // reset the player
    this.player && this.player.remove();

    // update the saved stream url
    this.streamUrl = streamUrl;

    this.dom.classList.remove('not-available');
    this.dom.classList.add('available');

    const jwOptions = {
      file: streamUrl,
      height: 40,
      aspectratio: null,
      width: '100%',
      autostart: true,
      mute: false,
      primary: 'html5',
      skin: {
        name: this.options.skin,
        active: this.options.activecolor,
      },
      doNotSaveCookies: true,
      title: this.options.title,
      events: {
        onSetupError: (e) => {
          Trace(e);
          this._disablePlayer();
        },
        onError: (e) => {
          Trace(e);
          this._disablePlayer();
        },
      },
    };

    const jwplayer = await loadJwPlayer();
    this.player = jwplayer(this.options.playerId).setup(jwOptions);
  }

  _disablePlayer() {
    this.dom.classList.remove('available');
    this.dom.classList.add('not-available');
    this.player && this.player.remove();
  }

  _exitFunction = () => {
    this.interval && clearInterval(this.interval);
    AudioService.open = false;
  };
}
